/**
 * New Year stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearStickersPack: ToilePack = {
    slug: 'new-year-stickers',
    category: 'season',
    name: 'New Year stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'bottle', label: 'Popping bottle', subject: 'A champagne bottle with the cork flying and a burst of bubbles, a thick white die-cut border'},
        {key: 'hat', label: 'Party hat', subject: 'A striped cone party hat with a pompom and confetti, a thick white die-cut border'},
        {key: 'clock', label: 'Clock at twelve', subject: 'A round clock with both hands at twelve and small stars around it, a thick white die-cut border'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['a disco ball instead', 'with glitter texture', 'as a matching sticker pair'],
    directionHint: 'try: a disco ball instead',
    seo: {
        title: 'New Year Stickers: Bottle, Hat & Clock',
        metaDescription: 'Nine die-cut New Year stickers — popping champagne bottle, party hat, clock at twelve — in art deco, neon party, and minimal styles.',
        h1: 'New Year stickers: champagne bottle, party hat, clock at twelve',
        tagline: 'Nine stickers, three motifs, three treatments each.',
        intro: 'Nine die-cut New Year stickers, each with a thick white border: a champagne bottle popping with a burst of bubbles, a striped party hat with confetti, and a round clock with both hands at twelve, in an art deco, a neon party, and a minimal treatment. Ready to print and cut, or drop straight onto a digital planner.',
        useCases: [
            'a printable sticker sheet for a planner or journal',
            "a New Year's Eve party favor or invitation seal",
            'a countdown card or place-card accent',
            'a digital sticker for a tablet planning app',
        ],
        faq: [
            {q: 'Are these already die-cut, or do I cut them myself?', a: 'Each sticker is drawn with its white die-cut border built in, ready to print and cut out yourself with scissors or a cutting machine.'},
            {q: 'Can I print these at home?', a: 'Yes — each file is a clean isolated image on a plain background, sized to print at typical sticker-paper dimensions.'},
            {q: "What's the difference between the three styles?", a: 'Art deco uses geometric fans and sunbursts in black and gold, neon party uses glowing lines and confetti on deep navy, and minimal keeps to gold and white with generous space.'},
            {q: 'Can I use these in a digital planner app?', a: 'Yes — the transparent-friendly PNGs drop straight into GoodNotes, Notability, or any app that accepts image stickers.'},
        ],
    },
}
